import React, { useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { GetMicrostoreSitesQuery, useGetMicrostoreColumnGroupsQuery, useMoveColumnMutation } from 'data/__generated__';
import { SiteSelect } from 'components/Select/SiteSelect';

export function MoveColumnPopup({
    isOpen,
    setMicrostoreColumnToMove,
    column,
}: {
    isOpen: boolean;
    setMicrostoreColumnToMove: (
        column:
            | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
            | null,
    ) => void;
    column:
        | GetMicrostoreSitesQuery['microstoreSitesWithColumnGroups'][number]['microstoreColumnGroups'][number]['columns'][number]
        | null;
}) {
    const [positionInColumnGroup, setPositionInColumnGroup] = useState<number>(column?.positionInColumnGroup ?? 0);
    const [siteId, setSiteId] = useState<string | null>(column?.siteId ?? null);
    const [columnGroupId, setColumnGroupId] = useState<string | null>(column?.columnGroupId ?? null);

    const notInitialRender = useRef<boolean>(false); // hack to ignore useEffect during initial render (setting state in select onChange doesn't work)

    const { data: columnGroupsData, refetch } = useGetMicrostoreColumnGroupsQuery({
        variables: {
            siteId: siteId ?? '',
        },
        fetchPolicy: 'cache-and-network',
    });

    const columnGroups = columnGroupsData?.microstoreColumnGroups || [];

    useEffect(() => {
        if (notInitialRender.current) {
            setPositionInColumnGroup(columnGroups.find(({ _id }) => _id === columnGroupId)?.columns.length as number);
        } else {
            notInitialRender.current = true;
        }
    }, [columnGroupId, columnGroups]);

    useEffect(() => {
        if (siteId) {
            refetch({ siteId });
        }
    }, [siteId, refetch]);

    const [moveColumn, { loading: updateLoading }] = useMoveColumnMutation();

    async function handleSubmit() {
        if (!siteId) {
            toast.error('Veuillez sélectionner un site');
            return;
        }

        if (!columnGroupId) {
            toast.error('Veuillez sélectionner un groupe de colonne');
            return;
        }

        const { data } = await moveColumn({
            variables: {
                columnId: column?._id ?? '',
                siteId,
                columnGroupId,
                positionInColumnGroup,
            },
        });

        if (!data) {
            toast.error('Une erreur est survenue lors de la modification de la colonne');
            return;
        }

        const {
            moveColumnMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        setMicrostoreColumnToMove(null);
    }

    const positionInColumnGroupOptions = useMemo(() => {
        const selectedColumnGroup = columnGroups.find(({ _id }) => _id === columnGroupId);
        return selectedColumnGroup
            ? Array(selectedColumnGroup.columns.length + (column?.columnGroupId === columnGroupId ? 0 : 1))
                  .fill(undefined)
                  .map((_, index) => ({
                      value: index,
                      label: `${index + 1}`,
                  }))
            : [];
    }, [columnGroupId, column?.columnGroupId, columnGroupsData]);

    const groupOptions = useMemo(
        () =>
            columnGroups
                .map(({ _id: columnGroupId, name }) => ({
                    value: columnGroupId,
                    label: name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) || [],
        [columnGroupsData],
    );

    return (
        <TotemPopup
            title="Changer de place une colonne"
            isOpen={isOpen}
            setIsOpen={() => setMicrostoreColumnToMove(null)}
            contentOverflow="visible"
        >
            <SubmitContainer>
                <SiteSelect value={siteId} onChange={setSiteId} />
                <TotemSelect
                    label="Groupe de colonne"
                    placeholder="Sélectionner groupe de collone"
                    value={columnGroupId}
                    options={groupOptions}
                    onChange={(value) => {
                        if (value) {
                            setColumnGroupId(value);
                        }
                    }}
                />
                <TotemSelect<number>
                    label="Position de la colonne"
                    placeholder="Sélectionner la position de la colonne"
                    value={positionInColumnGroup}
                    options={positionInColumnGroupOptions}
                    onChange={(value) => {
                        if (value !== null) {
                            setPositionInColumnGroup(value);
                        }
                    }}
                />
                <TotemPrimaryLoadingButton
                    loading={updateLoading}
                    onClick={handleSubmit}
                    useIcon={false}
                    label="Confirmer"
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
